import { useState } from 'react';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { fmtCents, todayEasternYmd } from './format.js';

// The confirm control shared by the sweep and allocation screens (sweep spec
// §3.3, money spec §6). It asks for the amount the human ACTUALLY moved,
// typed into an empty field (V4, R1), and the day it moved. The computed total
// sits beside the field for reading, never inside it.
//
// On a mismatch the server refuses and reports both figures; this renders
// them and opens the override, which needs a typed reason (R1 again: an
// override is a statement, not a click).
//
// `onConfirm({ amount_moved, moved_on, override_reason })` resolves to
// { ok: true } or { ok: false, mismatch: { computed, moved, difference } }.
export default function MoveConfirm({ computedTotal, count, verb, destination, disabled, onConfirm }) {
  const [amountMoved, setAmountMoved] = useState('');
  const [movedOn, setMovedOn] = useState(todayEasternYmd());
  const [overrideReason, setOverrideReason] = useState('');
  const [mismatch, setMismatch] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await onConfirm({
        amount_moved: amountMoved,
        moved_on: movedOn,
        override_reason: mismatch ? overrideReason : undefined,
      });
      if (r?.ok) {
        setAmountMoved('');
        setOverrideReason('');
        setMismatch(null);
      } else if (r?.mismatch) {
        setMismatch(r.mismatch);
      }
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = !disabled && count > 0 && amountMoved !== '' && movedOn && (!mismatch || overrideReason.trim().length > 0);

  return (
    <div className="rounded-card border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm text-text-muted">
          {count} row{count === 1 ? '' : 's'} selected. Computed: <span className="font-mono text-text">{fmtCents(computedTotal)}</span>
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <label className="text-xs text-text-muted">
          Amount you moved to {destination}
          <Input
            type="number"
            step="0.01"
            inputMode="decimal"
            data-money-input="amount_moved"
            value={amountMoved}
            onChange={(e) => setAmountMoved(e.target.value)}
            className="mt-1"
          />
        </label>
        <label className="text-xs text-text-muted">
          Day it moved
          <Input type="date" value={movedOn} onChange={(e) => setMovedOn(e.target.value)} className="mt-1" />
        </label>
        <div className="flex items-end">
          <Button onClick={submit} disabled={!canSubmit || busy} className="w-full">
            {busy ? 'Recording...' : `Record ${verb}`}
          </Button>
        </div>
      </div>
      {mismatch ? (
        <div className="mt-3 rounded-card border border-danger/40 bg-danger/5 p-3 text-sm">
          <div className="font-medium text-danger">Not recorded: the amount you typed does not match the computed total.</div>
          <div className="mt-1 font-mono text-xs text-text">
            Computed {fmtCents(mismatch.computed)} · Typed {fmtCents(mismatch.moved)} · Difference {fmtCents(mismatch.difference)}
          </div>
          <p className="mt-2 text-xs text-text-muted">
            Check the transfer and the selection first. To record it anyway, say why; the reason is kept beside both figures.
          </p>
          <Input
            data-money-input="override_reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            className="mt-2"
          />
        </div>
      ) : null}
    </div>
  );
}
